// src/components/Publications.js
import React, { useState, useEffect, useMemo } from 'react';
import { Cite } from '@citation-js/core';
import '@citation-js/plugin-bibtex';


// Import your icons
import { FaFilePdf, FaLink, FaChevronDown, FaChevronUp } from 'react-icons/fa6';

const publicationTypeCategories = [
  { value: 'journal', label: 'Journal Publications (articles)' },
  { value: 'conference', label: 'Conference Proceedings (proceedings)' },
  { value: 'other', label: 'Other Types' }
];


// Sub-component for rendering each publication card with collapsible abstract
const PublicationCardItem = ({ item, originalEntry, renderAuthorsFunc }) => {
  const [isAbstractOpen, setIsAbstractOpen] = useState(false); // State for this specific item



  const pdfLinkFromBib = originalEntry?.pdf; 
  const abstractText = originalEntry?.abstract || originalEntry?.note; 

  const authorsDisplay = renderAuthorsFunc(item.author || item.editor);

  const cslYear = item.issued && item.issued['date-parts'] ? item.issued['date-parts'][0][0] : '';
  let venue = item['container-title'] || item.event || item.publisher || '';
  let venueTypeDetails = '';
  if (item.type === 'report' && originalEntry?.properties?.type) {
    venueTypeDetails = originalEntry.properties.type;
  } else if (item.type === 'thesis' && originalEntry?.properties?.type) {
      venueTypeDetails = originalEntry.properties.type;
  }
  const volume = item.volume;
  const issue = item.issue;
  const pages = item.page;

  const hasDoi = !!item.DOI;
  const hasPdf = !!pdfLinkFromBib;
  const hasUrl = !!(item.URL && !pdfLinkFromBib && !item.DOI);
  const showIconGroup = hasDoi || hasPdf || hasUrl;
  
  // Ensure item.id is a stable, unique string. If not, generate a fallback.
  const abstractId = `abstract-${item.id || (originalEntry?.id || Math.random().toString(36).substr(2, 9))}`;


  return (
    <div className="card publication-list-item mb-3 shadow-sm">
      <div className="card-body">
        {/* Metadata section */}
        <h5 className="publication-title mb-1">{item.title || 'No Title'}</h5>
        <p className="publication-authors text-muted small mb-2">
          {authorsDisplay}
        </p>
        <p className="publication-venue small mb-2">
          {venue && <em>{venue}</em>}
          {venueTypeDetails && <span className="text-muted"> ({venueTypeDetails})</span>}
          {cslYear && <span className="text-muted"> ({cslYear})</span>}
          {volume && <span className="text-muted">, Vol. {volume}</span>}
          {issue && <span className="text-muted">, No. {issue}</span>}
          {pages && <span className="text-muted">, pp. {pages}</span>}
          {item.publisher && item.publisher !== venue && <span className="text-muted">. {item.publisher}</span>}
          {(!item.publisher || item.publisher === venue) && venue && '.'}
        </p>
        
        {/* Links integrated */}
        {showIconGroup && (
            <div className="publication-links-inline small mt-1 mb-2">
                {hasDoi && (
                <span className="publication-link-item me-3">
                    <a href={`https://doi.org/${item.DOI}`} target="_blank" rel="noopener noreferrer" title="View on DOI.org">
                    https://doi.org/{item.DOI}
                    </a>
                </span>
                )}
                {hasPdf && (
                <span className="publication-link-item me-3">
                    <a href={pdfLinkFromBib} target="_blank" rel="noopener noreferrer" title="Download PDF">
                    <FaFilePdf className="publication-link-icon me-1"/>PDF
                    </a>
                </span>
                )}
                {hasUrl && (
                <span className="publication-link-item me-3">
                    <a href={item.URL} target="_blank" rel="noopener noreferrer" title="View Online">
                    <FaLink className="publication-link-icon me-1"/>Link
                    </a>
                </span>
                )}
            </div>
        )}

        {/* Abstract Toggle and Collapsible Section */}
        {abstractText && (
          <>
            <button
              className="btn btn-sm btn-outline-secondary py-0 px-2 mt-2 d-flex align-items-center abstract-toggle-btn"
              onClick={() => setIsAbstractOpen(!isAbstractOpen)}
              type="button"
              aria-expanded={isAbstractOpen}
              aria-controls={abstractId} // Links button to the collapsible div for ARIA
            >
              {isAbstractOpen ? <FaChevronUp size="0.8em" className="me-1"/> : <FaChevronDown size="0.8em" className="me-1"/>}
              {isAbstractOpen ? 'Hide Abstract' : 'Show Abstract'}
            </button>
            {/* Collapsible div: Bootstrap's .collapse and .show classes control visibility and animation */}
            <div className={`collapse${isAbstractOpen ? ' show' : ''}`} id={abstractId}>
              <div className="publication-abstract small mt-2 pt-2 border-top" style={{ whiteSpace: 'pre-line' }}>
                {abstractText} {/* Render plain text; newlines in BibTeX abstract will create line breaks */}
                {/* If abstractText might contain Markdown: <ReactMarkdown>{abstractText}</ReactMarkdown> */}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


function Publications() {
  const [allPublications, setAllPublications] = useState([]);
  const [originalBibEntries, setOriginalBibEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedTypeCategory, setSelectedTypeCategory] = useState('');

  // useEffect for fetching and parsing BibTeX
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/publications.bib`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status} while fetching publications.bib`);
        return response.text();
      })
      .then(bibtexText => {
        if (!bibtexText.trim()) { setError("Fetched publications file is empty."); setAllPublications([]); return; }
        try {
          const cite = new Cite(bibtexText);
          setOriginalBibEntries(cite.data); // Essential for accessing original fields like 'abstract'
          const cslData = cite.get({ format: 'real', type: 'json', style: 'csl' });
          if (!cslData || !Array.isArray(cslData) || cslData.length === 0) { // Added Array.isArray check
            setError("No valid publications found after parsing with citation-js."); 
            setAllPublications([]); 
            return; 
          }
          const sortedPubs = cslData.sort((a, b) => {
            const yearA = a.issued && a.issued['date-parts'] ? parseInt(a.issued['date-parts'][0][0], 10) : 0;
            const yearB = b.issued && b.issued['date-parts'] ? parseInt(b.issued['date-parts'][0][0], 10) : 0;
            if (yearB !== yearA) return yearB - yearA;
            const titleA = a.title || ''; const titleB = b.title || '';
            return titleA.localeCompare(titleB);
          });
          setAllPublications(sortedPubs);
        } catch (e) {
          console.error("Error parsing BibTeX with citation-js:", e);
          setError("Failed to parse publications data. Check BibTeX format or console for details: " + e.message);
          setAllPublications([]);
        }
      })
      .catch(e => {
        console.error("Error fetching or processing BibTeX file:", e);
        setError(`Failed to load publications data: ${e.message}`);
        setAllPublications([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const renderAuthors = (cslAuthors) => {
    if (!Array.isArray(cslAuthors) || cslAuthors.length === 0) return 'N/A'; // Added Array.isArray
    const myName = "Michael Klesel"; const myLastName = "Klesel";
    const authorNamesString = cslAuthors.map(author => {
      if (!author) return ''; // Handle cases where an author object might be null/undefined in the array
      let name = '';
      if (author.given) name += author.given;
      if (author.family) name = (name ? name + ' ' : '') + author.family;
      if (!name && author.literal) name = author.literal;
      return name.trim();
    }).join(', ');
    return authorNamesString.split(', ').map((authorName, index, arr) => {
      if (typeof authorName !== 'string') return null; // Safety check
      const isMyName = authorName.toLowerCase().includes(myName.toLowerCase()) || (authorName.includes(myLastName) && (authorName.includes("Michael") || authorName.includes("M.")));
      return (<span key={authorName + index}>{isMyName ? <strong style={{ color: 'var(--link-color)' }}>{authorName}</strong> : authorName}{index < arr.length - 1 ? ', ' : ''}</span>);
    });
  };
  
  const handleYearChange = (event) => setSelectedYear(event.target.value);
  const handleTypeChange = (event) => setSelectedTypeCategory(event.target.value);
  
  const uniqueYearsForFilter = useMemo(() => {
    if (!Array.isArray(allPublications) || allPublications.length === 0) return [];
    const yearsSet = new Set(allPublications.map(pub => (pub.issued && pub.issued['date-parts'] ? pub.issued['date-parts'][0][0].toString() : 'Undated')));
    return Array.from(yearsSet).sort((a, b) => {
      if (a === 'Undated') return 1; if (b === 'Undated') return -1;
      return parseInt(b, 10) - parseInt(a, 10);
    });
  }, [allPublications]);

  const displayedPublications = useMemo(() => {
    const sourcePublications = Array.isArray(allPublications) ? allPublications : [];
    let filtered = sourcePublications;
    if (selectedYear) {
      filtered = filtered.filter(pub =>
        (pub.issued && pub.issued['date-parts'] ? pub.issued['date-parts'][0][0].toString() : 'Undated') === selectedYear
      );
    }
    if (selectedTypeCategory) {
      switch (selectedTypeCategory) {
        case 'journal':
          filtered = filtered.filter(pub => pub.type === 'article-journal');
          break;
        case 'conference':
          filtered = filtered.filter(pub => pub.type === 'paper-conference' || pub.type === 'chapter');
          break;
        case 'other':
          filtered = filtered.filter(pub => pub.type !== 'article-journal' && pub.type !== 'paper-conference' && pub.type !== 'chapter');
          break;
        default:
          break;
      }
    }
    return filtered;
  }, [allPublications, selectedYear, selectedTypeCategory]);


  if (loading) return <div className="container mt-5 text-center"><p>Loading publications...</p></div>;
  if (error) return <div className="container mt-5 alert alert-danger" role="alert"><p>{error}</p></div>;
  if (allPublications.length === 0 && !loading) return <div className="container mt-5"><p>No publications found.</p></div>; // This could also mean the bib file is empty or has no processable entries

  return (
    <div className="container mt-5 mb-5">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-md-12 publications-main-content">
          <h1 className="mb-4">Publications</h1>
          {/* Filters Row */}
          {allPublications.length > 0 && (
            <div className="row mb-4 g-3 align-items-end">
              <div className="col-md-4 col-sm-6">
                 <label htmlFor="year-filter" className="form-label fw-semibold">Filter by Year:</label>
                <select id="year-filter" className="form-select shadow-sm" value={selectedYear} onChange={handleYearChange} aria-label="Filter publications by year">
                  <option value="">All Years</option>
                  {uniqueYearsForFilter.map(year => (<option key={year} value={year}>{year}</option>))}
                </select>
              </div>
              <div className="col-md-4 col-sm-6">
                <label htmlFor="type-filter" className="form-label fw-semibold">Filter by Type:</label>
                <select id="type-filter" className="form-select shadow-sm" value={selectedTypeCategory} onChange={handleTypeChange} aria-label="Filter publications by type">
                  <option value="">All Types</option>
                  {publicationTypeCategories.map(category => ( <option key={category.value} value={category.value}>{category.label}</option> ))}
                </select>
              </div>
            </div>
          )}

          {displayedPublications.length === 0 && (selectedYear !== '' || selectedTypeCategory !== '') && (
             <p className="mt-3">No publications found matching your selected filters.</p>
          )}

          {(displayedPublications.length > 0) && (() => {
            const finalGrouped = displayedPublications.reduce((acc, pub) => {
              const yearString = pub.issued && pub.issued['date-parts'] ? pub.issued['date-parts'][0][0].toString() : 'Undated';
              if (!acc[yearString]) acc[yearString] = [];
              acc[yearString].push(pub);
             return acc;
            }, {});
            const finalYears = Object.keys(finalGrouped).sort((a, b) => {
                if (a === 'Undated') return 1; if (b === 'Undated') return -1;
                return parseInt(b, 10) - parseInt(a, 10);
            });

            return finalYears.map(year => (
              <div key={year} className="mb-5 year-section">
                {(!selectedYear || finalYears.length > 1) &&
                  <h2 id={`year-${year}`} className="h3 mb-4 pt-3 border-top mt-4">{year}</h2>
                }
                {finalGrouped[year].map((itemCSL) => { // Renamed to itemCSL for clarity
                  const originalBibEntry = originalBibEntries.find(orig => orig.id === itemCSL.id);
                  return (
                    <PublicationCardItem
                      key={itemCSL.id}
                      item={itemCSL} // Pass the CSL-JSON item
                      originalEntry={originalBibEntry} // Pass the corresponding original entry
                      renderAuthorsFunc={renderAuthors}
                    />
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
}

export default Publications;